/**
 * Validación de dominio email contra whitelist (defensa en profundidad).
 *
 * Capas:
 *   1. Cliente (AccessCard) — valida ANTES de invocar Supabase Auth.
 *      Evita pedirle a Supabase enviar magic-link a dominios random.
 *   2. Servidor — Supabase Auth dashboard tiene su propia config de
 *      "Allowed email domains" (apply en Project Settings → Auth →
 *      Email).
 *   3. (Opcional v1.1) BFF — endpoint `/v1/auth/request-magic-link` que
 *      valida domain + emite OTP via Admin API. Ahora cliente va directo
 *      a Supabase, así que esta capa NO existe todavía.
 *
 * Configuración: lista CSV en variable de entorno
 *   `VITE_PUBLIC_ALLOWED_AUTH_DOMAINS` (frontend)
 *   `ALLOWED_AUTH_DOMAINS`            (BFF)
 *
 * Lista vacía o no configurada → permite cualquier dominio (modo dev).
 * En producción la lista DEBE estar seteada.
 */

/** Normaliza string de dominios CSV a array. */
export function parseAllowedDomains(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}

/** Extrae el dominio de un email. `null` si formato inválido. */
export function emailDomain(email: string): string | null {
  if (typeof email !== 'string') return null;
  const trimmed = email.trim().toLowerCase();
  // RFC 5321 quick-and-dirty: exactamente un `@`, hay caracteres a ambos lados.
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;
  if (trimmed.indexOf('@') !== at) return null; // multiple @ → invalid
  const domain = trimmed.slice(at + 1);
  if (!domain.includes('.')) return null;
  return domain;
}

/**
 * Política: email permitido si:
 *   - su dominio está en `allowed` (match exacto o subdominio), o
 *   - su email completo está en `allowed` (excepción individual,
 *     útil para developers fuera del dominio corporativo).
 * Lista vacía → permite todo (modo dev sin configurar).
 *
 * Items con `@` se tratan como emails exactos (case-insensitive).
 * Items sin `@` se tratan como dominios (subdominio incluido).
 */
export function isAllowedAuthEmail(email: string, allowed: string[]): boolean {
  const domain = emailDomain(email);
  if (!domain) return false;
  if (allowed.length === 0) return true;
  const emailLower = email.trim().toLowerCase();
  return allowed.some((a) => {
    if (a.includes('@')) return a === emailLower;
    return domain === a || domain.endsWith(`.${a}`);
  });
}
